import { IdentificationContainer } from '../identification';
import { PersonalInformationFields } from '../personal-information';
import { createSchema } from './schema';
import { ApplicationData, ApplicationDataStore } from '../application/ApplicationDataStore';
import { LoanApplicantIdDocumentType } from '@servicetitan/financing-api-client';
import { SaveFormDataToStore } from '../application/SaveFormDataToStore';
import { HasCoApplicantFieldWatcher } from './HasCoApplicantFieldWatcher';
import { CoApplicantMailingAddressFields } from './CoApplicantMailingAddressFields';
import { Flex } from '@servicetitan/anvil2';
import merge from 'lodash.merge';
import { Form, FormProps } from '../common/Form';
import { Radio, RadioGroup } from '@servicetitan/anvil2-react-hook-form';
import {
    useProviderConfig,
    useSecondLookIntegration,
} from '@servicetitan/financing-workflow-shared';
import { useMemo } from 'react';

export const Container = (
    props: Omit<
        FormProps<ApplicationData['coApplicant']>,
        'schema' | 'defaultValues' | 'formData' | 'children'
    >,
) => {
    const { applicationData } = ApplicationDataStore.useStore();
    const config = useProviderConfig();
    const secondLookIntegration = useSecondLookIntegration();
    const { additionalApplicantFields = [] } = config;
    const requiresIdentification = additionalApplicantFields.includes('identification');

    const { secondLookProviderConfig } = secondLookIntegration;

    const primaryApplicantState = config.hasMailingAddressStep
        ? (applicationData.mailingAddress?.primaryApplicant?.address?.state ?? '')
        : (applicationData.applicant.primaryApplicant?.jobDetails?.address?.state ?? '');

    const defaultValues = merge(
        {},
        {
            coApplicant: {
                isAddressSameAsPrimary: true,
                applicantId: {
                    applicantIdDocumentType: LoanApplicantIdDocumentType.DriversLicense,
                },
            },
        },
        applicationData.coApplicant,
    );

    const schema = useMemo(() => {
        return createSchema({ ...config, primaryApplicantState, secondLookProviderConfig });
    }, [config, primaryApplicantState, secondLookProviderConfig]);

    return (
        <Form {...props} formData={defaultValues} schema={schema}>
            <SaveFormDataToStore fieldPrefix="coApplicant" defaultValues={defaultValues} />
            <RadioGroup legend="Is there a co-applicant?" name="hasCoApplicant">
                <Radio
                    name="hasCoApplicant"
                    // eslint-disable-next-line react/jsx-boolean-value
                    value={true}
                    label="Yes"
                    type="boolean"
                />
                <Radio name="hasCoApplicant" value={false} label="No" type="boolean" />
            </RadioGroup>
            <HasCoApplicantFieldWatcher>
                {isCoapplicant =>
                    isCoapplicant && (
                        <Flex direction="column" gap="10">
                            <PersonalInformationFields fieldPrefix="coApplicant" />
                            {requiresIdentification && (
                                <IdentificationContainer fieldPrefix="coApplicant" />
                            )}
                            <CoApplicantMailingAddressFields
                                primaryApplicantState={primaryApplicantState}
                            />
                        </Flex>
                    )
                }
            </HasCoApplicantFieldWatcher>
        </Form>
    );
};
